import React, { useState, ReactNode } from 'react';
import { Minimize2, Maximize2, LucideIcon } from 'lucide-react';

interface OnboardingStatusBannerProps {
  /** Icon component to display */
  icon: LucideIcon;
  /** Main title text */
  title: string;
  /** Optional description or children for custom content */
  children?: ReactNode;
  /** Color scheme - determines gradient and border colors */
  variant?: 'yellow' | 'purple' | 'blue' | 'green' | 'orange' | 'pink' | 'teal';
  /** Optional custom icon animation */
  iconAnimation?: React.CSSProperties;
  /** Optional icon class name for custom styling */
  iconClassName?: string;
  /** Whether the banner should be minimized by default */
  defaultMinimized?: boolean;
}

const variantStyles = {
  yellow: {
    container: 'bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-[#1a1500] dark:to-[#1f1a00] border-yellow-300 dark:border-yellow-700/30',
    icon: 'text-yellow-600 dark:text-yellow-400',
    title: 'text-yellow-900 dark:text-yellow-300',
    content: 'text-yellow-800 dark:text-yellow-300/80',
    button: 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
  },
  purple: {
    container: 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-[#150018] dark:to-[#1a0015] border-purple-200 dark:border-purple-700/30',
    icon: 'text-purple-600 dark:text-purple-400',
    title: 'text-purple-900 dark:text-purple-300',
    content: 'text-purple-800 dark:text-purple-300/80',
    button: 'text-purple-600 dark:text-purple-400 hover:text-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/30',
  },
  blue: {
    container: 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-[#001018] dark:to-[#00141a] border-blue-200 dark:border-blue-700/30',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-900 dark:text-blue-300',
    content: 'text-blue-800 dark:text-blue-300/80',
    button: 'text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30',
  },
  green: {
    container: 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-[#001a0d] dark:to-[#001510] border-green-200 dark:border-green-700/30',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-900 dark:text-green-300',
    content: 'text-green-800 dark:text-green-300/80',
    button: 'text-green-600 dark:text-green-400 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30',
  },
  orange: {
    container: 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-[#1a0f00] dark:to-[#1f1500] border-orange-300 dark:border-orange-700/30',
    icon: 'text-orange-600 dark:text-orange-400',
    title: 'text-orange-900 dark:text-orange-300',
    content: 'text-orange-800 dark:text-orange-300/80',
    button: 'text-orange-600 dark:text-orange-400 hover:text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/30',
  },
  pink: {
    container: 'bg-gradient-to-r from-pink-50 to-rose-50 dark:from-[#1a0010] dark:to-[#1a000d] border-pink-300 dark:border-pink-700/30',
    icon: 'text-pink-600 dark:text-pink-400',
    title: 'text-pink-900 dark:text-pink-300',
    content: 'text-pink-800 dark:text-pink-300/80',
    button: 'text-pink-600 dark:text-pink-400 hover:text-pink-700 hover:bg-pink-100 dark:hover:bg-pink-900/30',
  },
  teal: {
    container: 'bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-[#001a18] dark:to-[#00181a] border-teal-300 dark:border-teal-700/30',
    icon: 'text-teal-600 dark:text-teal-400',
    title: 'text-teal-900 dark:text-teal-300',
    content: 'text-teal-800 dark:text-teal-300/80',
    button: 'text-teal-600 dark:text-teal-400 hover:text-teal-700 hover:bg-teal-100 dark:hover:bg-teal-900/30',
  },
};

export const OnboardingStatusBanner: React.FC<OnboardingStatusBannerProps> = ({
  icon: Icon,
  title,
  children,
  variant = 'yellow',
  iconAnimation,
  iconClassName = '',
  defaultMinimized = true,
}) => {
  const [isMinimized, setIsMinimized] = useState(defaultMinimized);
  const styles = variantStyles[variant];

  return (
    <div className={`${styles.container} border-b-2 py-3 px-6 3xl:px-14`}>
      <div className="w-full">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="hidden sm:block">
              <Icon className={`w-5 h-5 ${styles.icon} ${iconClassName}`} style={iconAnimation} />
            </div>
            <div className="flex-1">
              <div className="space-y-1">
                <p className={`font-semibold ${styles.title} text-sm`}>{title}</p>
                {!isMinimized && children && <div className={`${styles.content}`}>{children}</div>}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className={`${styles.button} transition-colors p-1 rounded flex-shrink-0`}
            aria-label={isMinimized ? 'Expand banner' : 'Minimize banner'}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
